"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ShieldCheck, Stethoscope } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 14
    },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        delay: shouldReduceMotion ? 0 : custom * 0.1,
        ease: [0.16, 1, 0.3, 1]
      }
    })
  };
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background py-12 md:py-20 lg:py-24 border-b border-border">
      {/* Subtle atmospheric ambient glow */}
      <div className="absolute top-0 right-0 -z-10 h-96 w-96 rounded-full bg-accent/5 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left Column: Editorial Headline & Actions */}
          <div className="lg:col-span-7 flex flex-col justify-center text-left">
            {/* Clinical Governance Eyebrow */}
            <motion.div custom={0} initial="hidden" animate="visible" variants={fadeInVariants} className="inline-flex items-center gap-2 self-start rounded-full bg-muted px-3.5 py-1.5 border border-border mb-6">
              <ShieldCheck className="size-4 text-chart-1" />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Evidence-informed pet care
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1 custom={1} initial="hidden" animate="visible" variants={fadeInVariants} className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.14] text-balance mb-6">
              Better answers for healthier, happier pets.
            </motion.h1>

            {/* Supporting Copy */}
            <motion.p custom={2} initial="hidden" animate="visible" variants={fadeInVariants} className="font-sans text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mb-8">
              Trusted guidance on dog and cat health, nutrition, behavior, breeds, products, and everyday pet care.
            </motion.p>

            {/* Dual Action Buttons */}
            <motion.div custom={3} initial="hidden" animate="visible" variants={fadeInVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-10">
              <Button asChild size="lg" className="h-12 px-7 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90 rounded-md shadow-sm transition-[color,background-color,box-shadow]">
                <Link to="/DogsPage" className="inline-flex items-center justify-center gap-2">
                  <span>Explore Dog Advice</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="h-12 px-7 text-base font-semibold bg-background text-foreground hover:bg-muted border-border rounded-md shadow-xs transition-[color,background-color,border-color]">
                <Link to="/CatsPage" className="inline-flex items-center justify-center gap-2">
                  <span>Explore Cat Advice</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </motion.div>

            {/* Editorial Standard Stamp */}
            <motion.div custom={4} initial="hidden" animate="visible" variants={fadeInVariants} className="pt-6 border-t border-border flex flex-wrap items-center gap-6 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <Stethoscope className="size-4 text-chart-1" />
                <span className="font-medium">Veterinary review process</span>
              </div>
              <div className="h-3 w-px bg-border hidden sm:block" />
              <div className="flex items-center gap-2">
                <span className="size-1.5 rounded-full bg-chart-1" />
                <span className="font-medium">Independent product evaluations</span>
              </div>
              <div className="h-3 w-px bg-border hidden sm:block" />
              <div className="flex items-center gap-2">
                <span className="size-1.5 rounded-full bg-chart-1" />
                <span className="font-medium">Research-backed advice</span>
              </div>
            </motion.div>
          </div>

          {/* Right Column: Natural Lifestyle Photo */}
          <div className="lg:col-span-5 relative">
            <motion.div custom={2} initial="hidden" animate="visible" variants={fadeInVariants} className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-lg">
              <div className="aspect-[4/3] sm:aspect-[16/11] lg:aspect-[4/4.2] w-full relative">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/hero_dog_and_cat_resting.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/hero_dog_and_cat_resting.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/hero_dog_and_cat_resting.png-wvc-sizes" alt="Dog and cat resting peacefully together in a bright, modern living room" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
              </div>

              {/* Clinical Verification Card Overlay */}
              <div className="absolute bottom-4 left-4 right-4 bg-background/95 backdrop-blur-sm border border-border p-4 rounded-xl shadow-md">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-muted text-chart-1 shrink-0">
                    <ShieldCheck className="size-5" />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-0.5">
                      Clinical standard
                    </div>
                    <p className="text-sm font-medium text-foreground leading-snug">
                      Every health guide is developed with veterinary clinical rigor.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}